import {
  IHostInsurancePolicy,
  ICreateHostInsurancePolicyRequest,
  IUpdateHostInsurancePolicyRequest,
  ILinkedHostInsurancePolicy,
} from '../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

// Same separate-host convention as HeavyDutyMachineryService's own heavyDutyMachineryBaseUrl.
const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** A host's own insurance policy catalog -- see HostInsurancePolicyController.GetMyPolicies. */
export const getMyHostInsurancePolicies = async (): Promise<IHostInsurancePolicy[]> => {
  const response = await apiService<IHostInsurancePolicy[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your insurance policies');
};

export const createHostInsurancePolicy = async (request: ICreateHostInsurancePolicyRequest): Promise<IHostInsurancePolicy> => {
  const response = await apiService<IHostInsurancePolicy>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create insurance policy');
};

/** Also how a host deactivates one (request.isActive = false) -- see IUpdateHostInsurancePolicyRequest's own doc comment. */
export const updateHostInsurancePolicy = async (id: string, request: IUpdateHostInsurancePolicyRequest): Promise<IHostInsurancePolicy> => {
  const response = await apiService<IHostInsurancePolicy>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_BY_ID(id),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update insurance policy');
};

/** Real delete -- rejected by the backend with a friendly message if the policy is still linked to a machine or has booking history; unlink and deactivate it instead (see updateHostInsurancePolicy). */
export const deleteHostInsurancePolicy = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete insurance policy');
};

/** The insurance policies linked to one specific machine -- owner-only management view (can include a link to a since-deactivated policy), NOT the guest-facing checkout option (see HeavyDutyMachineryService.getAvailableInsurancePolicy for that). */
export const getInsurancePoliciesForMachinery = async (machineryId: string): Promise<ILinkedHostInsurancePolicy[]> => {
  const response = await apiService<ILinkedHostInsurancePolicy[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_FOR_MACHINERY(machineryId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load insurance policies for this machine');
};

/**
 * Attaches (or replaces) the actual policy document behind a host's own
 * insurance offer. Kicks the policy back to Pending review: a new document
 * is a new claim to check, and letting a Verified policy quietly swap its
 * evidence would make the verification meaningless.
 */
export const uploadHostInsurancePolicyDocument = async (id: string, file: File): Promise<IHostInsurancePolicy> => {
  const formData = new FormData();
  formData.append('document', file);

  const response = await apiUploadService<IHostInsurancePolicy>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_DOCUMENT(id),
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to upload the policy document');
};
